import { Component, OnInit } from '@angular/core';
import { BreakpointObserver, Breakpoints } from '@angular/cdk/layout';
import { Observable } from 'rxjs';
import { map, pairwise, shareReplay } from 'rxjs/operators';
import { CallerService } from './services/caller.service';
import { OnlineStatusService } from './services/online-status.service';
import { profileUpdateSuccess, setCorpMemberProfileData } from './ngrx-store/actions/corp-member.actions';

@Component({
  selector: 'app-root',
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.css']
})
export class AppComponent {
  constructor(
    private callerService: CallerService,
    private onlineStatusService: OnlineStatusService,
  ) {
    // Notify on offline/online transitions - resolvers/guards can silently stall navigation
    // when a network call fails, so a clear reason beats a page that just looks unresponsive.
    this.onlineStatusService.isOnline$
      .pipe(pairwise())
      .subscribe(([wasOnline, isOnline]) => {
        if (wasOnline && !isOnline) {
          this.callerService.showNotification(
            "You're offline. Some pages and actions won't work until your connection is back.",
            0,
            'OK',
          )
        } else if (!wasOnline && isOnline) {
          this.callerService.showNotification("You're back online.", 3000, 'OK')
        }
      })

    const corpMemberLocal = localStorage.getItem(CallerService.LOCAL_STORAGE_DATA_KEY)
    // The profile cache (localStorage) outlives the auth token (sessionStorage — cleared when
    // the browser session ends, not shared across tabs). Rehydrating the profile without a
    // matching token used to leave the store in a "logged in" state the app can never actually
    // authenticate as: REST calls silently drop the Authorization header, and the chat socket's
    // handshake gets rejected by the backend forever with no visible error, permanently stuck
    // showing "offline" even though the backend is reachable — see SocketIoChatNamespaceService.
    const hasToken = Boolean(sessionStorage.getItem(CallerService.SESSION_STORAGE_DATA_KEY))

    if (corpMemberLocal && hasToken) {
      try {
        const savedSessionData = JSON.parse(corpMemberLocal)
        // SocketEffects.connectSocketsOnProfileSet$ reacts to this dispatch and connects the
        // sockets — including reconnecting on a fresh page load for an already-logged-in session,
        // since a reload doesn't re-run login()/signup().
        this.callerService.store.dispatch(setCorpMemberProfileData({data: savedSessionData}))
        this.refreshProfile()
      } catch (error) {
        // Handle JSON parsing error. Probably malformed JSON or not a JSON string
        // Clear the localStorage item if parsing fails, this prevents the app from crashing due to invalid JSON
        localStorage.removeItem(CallerService.LOCAL_STORAGE_DATA_KEY);
        console.error('Error parsing corpMemberLocal:', error)
      }
    } else if (corpMemberLocal && !hasToken) {
      localStorage.removeItem(CallerService.LOCAL_STORAGE_DATA_KEY)
    }
  }

  /**
   * The cached profile paints the app straight away, but it's only as fresh as the last login -
   * so onboarding progress, snoozes etc. changed on another device (or in the database) would
   * never show up. Merge in the backend's copy once per load. profileUpdateSuccess (rather than
   * setCorpMemberProfileData) also updates the cache without reconnecting the sockets. A 401
   * here goes through the usual logout in error-response.interceptor.
   */
  private refreshProfile(): void {
    this.callerService.getMyProfile().subscribe({
      next: (res: any) => {
        if (res?.body?.data) {
          this.callerService.store.dispatch(profileUpdateSuccess({ data: res.body.data }))
        }
      },
      error: (err) => console.warn('Could not refresh the profile; using the cached copy', err),
    })
  }
}
